import React, {useContext, useEffect, useRef, useState} from 'react';
import {CaptureContext, ModuloContext, SellerContext} from '../context/seller_context';
import '../assets/styles/face_recognition.css';
import * as faceapi from 'face-api.js';
import {useNavigate} from "react-router-dom";
import {MyContext} from "../context/my_context";

const FaceRecognition = () => {
    const sellerContext = useContext(SellerContext);
    const moduloContext = useContext(ModuloContext);
    const captureContext = useContext(CaptureContext);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [isRecognizing, setIsRecognizing] = useState(false);
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [knownDescriptors, setKnownDescriptors] = useState<any[]>([]);
    const [knownFaceImage, setKnownFaceImage] = useState<string | null>(null);
    let interval: NodeJS.Timeout | undefined;
    const navigate = useNavigate();
    const { seller } = sellerContext!;
    const { setCapturedImageC, hasCapturedImageC } = captureContext!;
    const [proxiedKnownFaceImage, setProxiedKnownFaceImage] = useState<string | null>(null);

    const context = useContext(MyContext);

    if (!context) {
        throw new Error("UserProfile must be used within a MyProvider");
    }

    const { userData } = context;

    // Function to load a known image and generate its descriptor
    // Initial proposal without CORS issue
    const loadKnownFace2 = async (imagePath: string): Promise<any | null> => {
        try {
            const img = await faceapi.fetchImage(imagePath);
            console.log("img", img);
            setKnownFaceImage(img.src);
            const detection = await faceapi.detectSingleFace(img).withFaceLandmarks().withFaceDescriptor();
            if (!detection) {
                console.error('No se detectó ninguna cara en la imagen conocida.');
                return null;
            }
            return detection.descriptor;
        } catch (error) {
            console.error('Error al cargar el rostro conocido:', error);
            return null;
        }
    }

    // Extensión personalizada de fetchImage con soporte para headers
    async function fetchImageWithAuth(url: string, token: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {

            // 2. Configuración dinámica del entorno
            const isLocalProduction = process.env.REACT_APP_LOCAL_PRODUCTION === 'true';
            let BACKEND_URL;

            if (process.env.NODE_ENV === 'development') {
                BACKEND_URL = isLocalProduction
                    ? process.env.REACT_APP_BACK_DEV
                    : process.env.REACT_APP_BACK_URL;
            } else {
                BACKEND_URL = process.env.REACT_APP_BACK_URL;
            }

            const img = new Image();
            img.crossOrigin = 'Anonymous';

            // Configurar el proxy URL con el token
            const proxyUrl = `${BACKEND_URL}/api/proxy-image?url=${encodeURIComponent(url)}`;

            // Usamos fetch para incluir los headers de autenticación
            fetch(proxyUrl, {
                headers: {
                    'x-access': token,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            })
                .then(response => {
                    if (!response.ok) throw new Error('Error al cargar imagen');
                    return response.blob();
                })
                .then(blob => {
                    const objectUrl = URL.createObjectURL(blob);
                    img.src = objectUrl;

                    img.onload = () => {
                        // URL.revokeObjectURL(objectUrl); // Limpiar memoria
                        resolve(img);
                    };

                    img.onerror = (err) => {
                        URL.revokeObjectURL(objectUrl);
                        reject(new Error(`Error al cargar imagen: ${err}`));
                    };
                })
                .catch(reject);
        });
    }

    // Load a known face image and generate its descriptor
    const loadKnownFace3 = async (imagePath: string): Promise<faceapi.LabeledFaceDescriptors | null> => {
        try {
            if (!userData?.token) throw new Error('No hay token de autenticación');

            const img = await fetchImageWithAuth(imagePath, userData.token);
            const detection = await faceapi.detectSingleFace(img)
                .withFaceLandmarks()
                .withFaceDescriptor();

            setProxiedKnownFaceImage(img.src);

            if (!detection) {
                console.warn('No se detectaron rostros en la imagen');
                return null;
            }

            return new faceapi.LabeledFaceDescriptors("known", [detection.descriptor]);
        } catch (error) {
            console.error('Error en loadKnownFace3:', error);
            return null;
        }
    };

    // Using the function to load a known image and get its descriptor
    const loadFaceDescriptorsAws = async () => {
        let knownDescriptorAws = null;
        if (seller?.foto_ventero) {
            console.log('Rostro desde la Api:', seller?.foto_ventero);
            knownDescriptorAws = await loadKnownFace3(seller.foto_ventero);

            if (knownDescriptorAws) {
                setKnownDescriptors([knownDescriptorAws]);
                setKnownFaceImage(seller.foto_ventero);
            }
        } else {
            console.log('No hay descriptores conocidos');
        }
    };

    console.log('proxiedKnownFaceImage', proxiedKnownFaceImage);

    // Load known models and descriptors
    useEffect(() => {
        //const MODEL_URL = process.env.REACT_APP_MODEL_URL || '/models';
        // Use a different relative path depending on the environment (development or production)
        const MODEL_URL = process.env.NODE_ENV === 'development'
            ? process.env.REACT_APP_MODEL_URL // For development, use localhost
            : process.env.REACT_APP_LOCAL_PRODUCTION === 'true'
                ? process.env.REACT_APP_MODEL_URL2 // For "local production" use localhost, the path is /build/models
                : process.env.REACT_APP_MODEL_URL2; // For real production, use the correct domain

        const loadModels = async () => {
            try {
                console.log('Esperando 2 segundos antes de cargar los modelos...');
                await new Promise((resolve) => setTimeout(resolve, 2000)); // Esperar 2 segundoa
                console.log("Cargando modelo desde:", MODEL_URL);
                await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
                console.log("Modelos #1 cargados");
                await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
                console.log("Modelos #2 cargados");
                await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
                console.log("Modelos #3 cargados");
                console.log('Modelos cargados exitosamente');

                // Load the known face descriptors
                if (seller?.foto_ventero) {
                    await loadFaceDescriptorsAws();
                }
                setModelsLoaded(true);

            } catch (error) {
                console.error('Error al cargar los modelos:', error);
            }
            await startRecognitionReference();
        };
        loadModels();
    }, [seller?.foto_ventero]);

    useEffect(() => {
        if (isRecognizing) {
            console.log('Renderizando video');
        }
    }, [isRecognizing]);

    // Start face recognition
    const startRecognitionReference = async () => {
        setIsRecognizing(true);
        setCapturedImage(null);
    };

    useEffect(() => {
        if (isRecognizing) {
            if (videoRef.current) {
                const startRecognition = async () => {
                    if (!modelsLoaded || knownDescriptors.length === 0) {
                        console.error('Los modelos no están cargados o no hay descriptores conocidos!');
                        return;
                    }

                    try {
                        // Request access to the camera
                        // @ts-ignore
                        videoRef.current.srcObject = await navigator.mediaDevices.getUserMedia({video: true});

                        // Timer to redirect after 10 seconds if no acknowledgement
                        const timeoutId = setTimeout(() => {
                            console.log("No se detectó ninguna coincidencia en 10 segundos. Redirigiendo a vista de Modulo y Ventero...");
                            handleClick01();
                        }, 10000); // 10 seconds

                        // Add a listener for when the video is playing
                        // @ts-ignore
                        videoRef.current.addEventListener('play', () => {
                             interval = setInterval(async () => {
                                if (videoRef.current) {
                                    const detections = await faceapi.detectAllFaces(videoRef.current)
                                        .withFaceLandmarks()
                                        .withFaceDescriptors();

                                    if (detections && detections.length > 0) {
                                        // Descriptors are numerical representations (vectors) of the face that capture unique features of the face.
                                        // They are generated by a facial recognition model and are used to compare different faces.
                                        // Detect faces in a video and calculates a descriptor for each one.
                                        const descriptors = detections.map(d => d.descriptor);
                                        const faceMatcher = new faceapi.FaceMatcher(knownDescriptors);

                                        // Results - They are the result of comparing the detected face descriptors with the known face descriptors, and are obtained using a FaceMatcher
                                        const results = descriptors.map(descriptor => faceMatcher.findBestMatch(descriptor));

                                        // Check for a positive match
                                        const bestMatch = results[0];
                                        if (bestMatch.label !== 'unknown' && bestMatch.distance < 0.6) { // Adjust the threshold as needed
                                            const matchPercentage = (1 - bestMatch.distance) * 100;
                                            console.log(`Porcentaje de coincidencia: ${matchPercentage.toFixed(2)}%`);

                                            alert('¡Coincidencia positiva detectada!');
                                            clearTimeout(timeoutId);
                                            captureImage(); // Capture the image
                                            stopRecognition();
                                        }
                                    }
                                }
                            }, 2000); // Indicates that facial recognition will be executed every 2 seconds
                        });
                    } catch (error) {
                        console.error('Error al acceder a la cámara:', error);
                    }
                };
                startRecognition();
                // Clear the interval when recognition stops
                return () => {
                    if (interval) {
                        clearInterval(interval);
                    }
                };
            }
        }
    }, [isRecognizing]);

    // Function to capture the image of the video
    const captureImage = () => {
        if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (context && videoRef.current) {
                canvas.width = videoRef.current.videoWidth;
                canvas.height = videoRef.current.videoHeight;
                context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/png');
                setCapturedImage(dataUrl);
            }
        }
    };

    // End recognition
    const stopRecognition = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach(track => track.stop());
        }
        setIsRecognizing(false);
        clearInterval(interval);
    };

    const handleClick01 = () => {
        navigate('/seller_profile');
    };

    useEffect(() => {
        setCapturedImageC(null);
    }, []);

    useEffect(() => {
        if (capturedImage) {
            setCapturedImageC(capturedImage);
        }
    }, [capturedImage]);

    useEffect(() => {
        if (hasCapturedImageC === true) {
            const timer = setTimeout(() => {
                handleClick01();
            }, 5000); // 5000 ms = 5 seconds

            return () => clearTimeout(timer);
        }
    }, [hasCapturedImageC]);

    return (
        <div className="face-info-container">
            <div className="face-module-container">
                <div>
                    {/* Conditional to show the title only if there is no captured image */}
                    {!capturedImage && <h2>VERIFICANDO IDENTIDAD</h2>}
                </div>
                <div className="img-container">
                    {/* Video for the camera or captured image */}
                    {capturedImage ? (
                         <div>
                            <div className="title-captured">
                                <h3>IDENTIDAD VERIFICADA</h3>
                            </div>
                            <div>
                                <img src={capturedImage} alt="Imagen capturada" className="img-captured"/>
                            </div>
                         </div>
                    ) : (
                        isRecognizing && (
                            <div>
                                <h3>CAPTURANDO VIDEO</h3>
                                <div className="video-container">
                                    <video ref={videoRef} autoPlay muted className="video-container-video"/>
                                    <div className="corner top-left"></div>
                                    <div className="corner top-right"></div>
                                    <div className="corner bottom-left"></div>
                                    <div className="corner bottom-right"></div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Known image */}
                    {proxiedKnownFaceImage && capturedImage && (
                        <div>
                            <div>
                                <h3>IMAGEN DEL VENTERO ALMACENADA EN SISDEP</h3>
                            </div>
                            <div>
                                <img src={proxiedKnownFaceImage} alt="Imagen conocida" className="stored-img-sisdep"/>
                            </div>
                        </div>
                    )}

                    {/* Hidden canvas to capture the video image */}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
                <div className="buttons-container">
                    {/* Button to activate facial recognition */}
                    {modelsLoaded ? (
                        <div>
                            {modelsLoaded && !isRecognizing && (
                            <div>
                                <button className="recognition-button" onClick={startRecognitionReference}>
                                 Activar Reconocimiento Facial
                                </button>
                            </div>
                            )}
                            {isRecognizing && (
                            <div>
                                <button className="recognition-button" onClick={stopRecognition}>
                                Detener Reconocimiento Facial
                                </button>
                            </div>
                            )}
                        </div>
                        ) : (
                        <p>Cargando modelos de reconocimiento facial...</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FaceRecognition;



